import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Logo } from "./Logo";
import { NavAnchor } from "./Header";
import { BUSINESS, ADDRESS_LINES } from "@/lib/business-info";
import { navQueryOptions } from "@/lib/navigation.functions";
import { DEFAULT_FOOTER_NAV, groupFooterItems, type NavItem } from "@/lib/navigation";

type FooterColumn = { column: string; items: { key: string; label: string; href: string; openInNew: boolean }[] };

const FALLBACK: FooterColumn[] = DEFAULT_FOOTER_NAV.map((c) => ({
  column: c.column,
  items: c.items.map((i) => ({ key: `${c.column}${i.href}`, label: i.label, href: i.href, openInNew: false })),
}));

function useFooterNav(): FooterColumn[] {
  const { data } = useQuery(navQueryOptions());
  const items = (data ?? []).filter((i: NavItem) => i.area === "footer");
  if (!items.length) return FALLBACK;
  return groupFooterItems(items).map((g) => ({
    column: g.column,
    items: g.items.map((i) => ({ key: i.id, label: i.label, href: i.href, openInNew: i.open_in_new })),
  }));
}

export function Footer() {
  const columns = useFooterNav();

  return (
    <footer className="border-t border-border/60 bg-card/40 mt-24">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-10 w-10" />
              <span className="font-display text-lg font-semibold">
                Glow <span className="text-primary text-glow">Long</span>
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-sm text-muted-foreground">
              Glow-in-the-dark golf balls for night golf, gifts, events, and good times. Built for recreational fun — not officially tournament-approved.
            </p>
            <address className="mt-6 space-y-1 text-sm not-italic text-muted-foreground">
              <p>
                <a href={`tel:${BUSINESS.phoneHref}`} className="transition hover:text-foreground">
                  {BUSINESS.phone}
                </a>
              </p>
              <p>
                <a href={`mailto:${BUSINESS.email}`} className="transition hover:text-foreground">
                  {BUSINESS.email}
                </a>
              </p>
              {ADDRESS_LINES.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </address>
          </div>
          {columns.map((col) => (
            <FooterCol key={col.column} title={col.column}>
              {col.items.map((item) => (
                <li key={item.key}>
                  <NavAnchor
                    href={item.href}
                    openInNew={item.openInNew}
                    className="text-muted-foreground transition hover:text-foreground"
                  >
                    {item.label}
                  </NavAnchor>
                </li>
              ))}
            </FooterCol>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-border/60 pt-6 text-xs text-muted-foreground md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} Glow Long Golf, LLC. All rights reserved.</p>
          <p>Made for golfers who play after dark.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">{title}</h3>
      <ul className="mt-4 space-y-2 text-sm">{children}</ul>
    </div>
  );
}
